import React, {Fragment, useContext, useEffect, useState} from "react";
import BoardNumberShow from "./BoardNumberShow";
import {Container, Pagination, PaginationItem, PaginationLink, Row} from "reactstrap";
import {Col} from "react-bootstrap";
import Loader from "../../../../../Layout/Loader";
import axiosConfig from "../../../../../Config/axios";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import type {BoardNumber, BoardNumberIndexProps} from "../../Types/BoardTypes";

const DEFAULT_PER_PAGE = 100;

const BoardNumberIndex: React.FC<BoardNumberIndexProps> = ({params, isLocked, callBack}) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [boardNumbers, setBoardNumbers] = useState<BoardNumber[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
    const [totalRows, setTotalRows] = useState<number>(0);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};
    const normalizedParams = (params || {}) as Record<string, unknown>;
    const hasParamsValues = Object.values(normalizedParams).some((value) => value !== undefined && value !== null);

    const getBoardNumbers = (page?: number, limit?: number) => {
        const currentPageToUse = page ?? currentPage;
        const limitToUse = limit ?? perPage;

        setLoaderVisibility(true);
        const config = {
            params: {
                ...normalizedParams,
                page: currentPageToUse,
                per_page: limitToUse,
            }
        };

        axiosConfig
            .get(getApplicationRoute('board-number'), config)
            .then((response: {
                data: {
                    data?: BoardNumber[] | {
                        data?: BoardNumber[];
                        meta?: { current_page?: number; per_page?: number; total?: number; };
                    }
                }
            }) => {
                const responseData = response?.data?.data;
                const boardNumberList = Array.isArray(responseData)
                    ? responseData
                    : Array.isArray(responseData?.data)
                        ? responseData.data
                        : [];
                const meta = !Array.isArray(responseData) ? responseData?.meta : undefined;

                setBoardNumbers(boardNumberList);
                setCurrentPage(meta?.current_page || currentPageToUse);
                setPerPage(meta?.per_page || limitToUse);
                setTotalRows(meta?.total || boardNumberList.length);
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setBoardNumbers([]);
                setTotalRows(0);
                setLoaderVisibility(false);
            });
    };

    useEffect(() => {
        if (hasParamsValues) {
            setCurrentPage(1);
            setPerPage(DEFAULT_PER_PAGE);
            getBoardNumbers(1, DEFAULT_PER_PAGE);
            return;
        }

        setBoardNumbers([]);
        setCurrentPage(1);
        setTotalRows(0);
    }, [hasParamsValues, params]);

    const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
    const shouldShowPagination = totalRows > perPage;

    return (
        <Container fluid={true}>
            <Loader visibility={loaderVisibility}/>
            <Fragment>
                <Row>
                    {boardNumbers?.map((boardNumber, key) => {
                        return <Col key={key} md={2}>
                            <BoardNumberShow
                                boardNumber={boardNumber}
                                isLocked={isLocked}
                                callBack={() => {
                                    getBoardNumbers(currentPage, perPage);
                                    callBack?.();
                                }}
                            />
                        </Col>
                    })}
                </Row>
                {shouldShowPagination && (
                    <Row className='mt-3'>
                        <Col className='d-flex justify-content-center'>
                            <Pagination>
                                <PaginationItem disabled={currentPage <= 1}>
                                    <PaginationLink previous onClick={() => getBoardNumbers(currentPage - 1, perPage)}/>
                                </PaginationItem>
                                <PaginationItem disabled>
                                    <PaginationLink>{`${currentPage} / ${totalPages}`}</PaginationLink>
                                </PaginationItem>
                                <PaginationItem disabled={currentPage >= totalPages}>
                                    <PaginationLink next onClick={() => getBoardNumbers(currentPage + 1, perPage)}/>
                                </PaginationItem>
                            </Pagination>
                        </Col>
                    </Row>
                )}
            </Fragment>
        </Container>
    )
}

export default BoardNumberIndex;